import {
  isTerminalBlastState,
  type BlastTripMetric,
  type EmailBlastSendingDomainPlanDto,
  type EmailBlastViewDto,
} from '@/types/emailBlast'
import { domainStopCause } from '@/utils/sendingDomainStatus'
import { formatCount } from '@/utils/sendingDomainWarmup'
import {
  asSentence,
  buildPlanDays,
  finishClause,
  nextBatchClause,
  planDomainState,
  stoppedDomainClause,
  stoppedDomainGuidance,
  todayClause,
  type BroadcastPlanDay,
} from './sendingDomainPlan'

// The one question the blast detail page exists to answer: is this send healthy, and do I need to
// intervene? A pure function of the view DTO and its sending domain's plan so it can be tested without a
// DOM — it is the only real logic in the ORB-706 rework, and it has more branches than anything else on
// the page.

export type VerdictTone = 'healthy' | 'attention' | 'scheduled' | 'finished' | 'stopped'

export interface BlastVerdict {
  tone: VerdictTone
  /** One sentence, with the numbers in it, answering "should I act, and why". */
  headline: string
  /** Only when there is something to do: what to check before resuming. */
  guidance?: string
  /**
   * What the state badge shows instead of the state, without a new enum value: Scheduled for a canary
   * that has not started, Paused for a broadcast whose sending domain stopped.
   */
  presentAs?: 'Scheduled' | 'Paused'
  /**
   * The sending domain decides when this sends (warming, stopped or setting up), so the test-batch mark
   * and the emails-per-minute pacing say nothing true about it.
   */
  pacedByDomain: boolean
  /** The broadcast's days while a warming domain spreads it over them; empty otherwise. */
  days: BroadcastPlanDay[]
  /** What the queued rows are doing, for the progress legend: "queued", "held", or waiting on the domain. */
  queuedLabel: string
  /**
   * The blast's recipient rows, counted from the rows themselves rather than the frozen audience.
   *
   * Deliberately NOT `TotalAudience - PulledAfterRegistrationCount`: the pulled-after-registration rows
   * keep `Status = Scheduled` with `Cancelled = true`, so they are ALREADY inside `Stats.Skipped`, and
   * subtracting them again double-counts them. Counting the buckets makes the bar sum to exactly this
   * number in every state, which is the property the segments depend on.
   */
  recipients: number
  /** Rows that have ever dispatched. Stats.Sent already IS this; never re-add the settled buckets. */
  sent: number
  /** Still scheduled and not cancelled — what is genuinely left to send. */
  queued: number
  /** Cancelled before send: environment guard, suppression, the breaker, or a post-staging registration. */
  skipped: number
  /** Never reached the provider at all (render/gate failure). Not inside `sent`. */
  failed: number
  /**
   * Everything that has not dispatched: queued + skipped + failed. On a live blast this is "still to
   * go"; on a stopped one it is "never sent", which is the only honest reading once the remainder has
   * been cancelled INTO the skipped bucket.
   */
  outstanding: number
  /** True for Canary before anything has shipped — materialization runs a 24h lookahead. */
  notStartedYet: boolean
}

function formatNumber(value: number): string {
  return value.toLocaleString()
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return 'an unknown time'
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? 'an unknown time' : parsed.toLocaleString()
}

function formatPercent(numerator: number, denominator: number): string {
  if (denominator <= 0) return '0%'
  return `${((numerator / denominator) * 100).toFixed(1)}%`
}

/** The gate decision that explains the CURRENT pause: the most recent one that actually tripped. */
export function latestTrip(blast: EmailBlastViewDto) {
  for (let index = blast.GateDecisions.length - 1; index >= 0; index--) {
    const decision = blast.GateDecisions[index]
    if (decision.TripMetric) return decision
  }
  return null
}

/**
 * Why the breaker paused it. Beside a sending domain that holds the send too, the sentence takes the
 * mockup's form ("over this broadcast's 2.50% limit") so the two limits on screen cannot be confused.
 */
function pausedReason(
  metric: BlastTripMetric | null,
  blast: EmailBlastViewDto,
  trip: ReturnType<typeof latestTrip>,
  besideDomain: boolean,
): string {
  const lead = besideDomain ? 'Paused automatically: ' : 'Paused automatically — '
  const whose = besideDomain ? "this broadcast's" : 'the'

  switch (metric) {
    case 'HardBounce':
      return (
        `${lead}hard bounces reached ${(trip?.HardBouncePercent ?? 0).toFixed(2)}%, ` +
        `over ${whose} ${blast.MaxHardBouncePercent.toFixed(2)}% limit.`
      )
    case 'Complaint':
      return (
        `${lead}spam complaints reached ${(trip?.ComplaintPercent ?? 0).toFixed(2)}%, ` +
        `over ${whose} ${blast.MaxComplaintPercent.toFixed(2)}% limit.`
      )
    case 'DeliveryRate':
      return (
        `${lead}only ${(trip?.DeliveredPercent ?? 0).toFixed(0)}% of the test batch was delivered, below the ` +
        `${blast.MinCanaryDeliveryPercent.toFixed(0)}% required before releasing the rest.`
      )
    case 'MinSendFloor':
      return (
        `${lead}only ${formatNumber(trip?.SampleSize ?? 0)} of the test batch actually sent, below the ` +
        `${formatNumber(blast.MinCanarySendsForRelease)} needed to judge it safely.`
      )
    default:
      // No TripMetric: either an operator paused it, or the row predates the field. Never invent a
      // cause — say what is true and let the Rollout log carry the detail.
      return besideDomain ? 'Paused.' : 'Paused —'
  }
}

function pausedGuidance(metric: BlastTripMetric | null, held: number): string {
  const resumeNote = ` Resuming sends to the remaining ${formatNumber(held)} under the same limits, so it can pause again.`

  switch (metric) {
    case 'HardBounce':
      return (
        'Hard bounces this high usually mean stale addresses. Check when this audience list was last ' +
        'updated before you resume.' + resumeNote
      )
    case 'Complaint':
      return (
        "Complaints this high usually mean the audience didn't expect this email. Check the targeting " +
        'and the subject line before you resume.' + resumeNote
      )
    case 'DeliveryRate':
      return (
        'A low delivery rate usually points at the sending domain rather than the list. Check your ' +
        'sending domains before you resume.' + resumeNote
      )
    case 'MinSendFloor':
      return (
        'Too few sends to measure. This usually means most of the audience was excluded — check the ' +
        'Delivery numbers below before you resume.' + resumeNote
      )
    default:
      return 'Check the Rollout log below for what the last gate check saw.' + resumeNote
  }
}

function pausedHeadline(
  metric: BlastTripMetric | null,
  blast: EmailBlastViewDto,
  held: number,
  trip: ReturnType<typeof latestTrip>,
): { headline: string; guidance: string } {
  const heldPhrase = `${formatNumber(held)} ${held === 1 ? 'email is' : 'emails are'} held and nothing more will send until you resume.`
  return {
    headline: `${pausedReason(metric, blast, trip, false)} ${heldPhrase}`,
    guidance: pausedGuidance(metric, held),
  }
}

/**
 * The blast's recipient rows, counted from the buckets. Exported so the Delivery funnel and the spine
 * cannot drift: they showed different "Recipients" totals on the same page while the funnel still
 * derived its own (review: Copilot, Aikido).
 */
export function countRecipients(blast: EmailBlastViewDto): number {
  const counted = blast.Stats.Sent + blast.Stats.Queued + blast.Stats.Skipped + blast.Stats.Failed
  return counted > 0 ? counted : Math.max(0, blast.TotalAudience - blast.PulledAfterRegistrationCount)
}

/**
 * `plan` is the broadcast's sending-domain plan (ORB-953), null until it loads or when it could not be
 * read; without one the verdict is the domain-blind one.
 */
export function buildBlastVerdict(
  blast: EmailBlastViewDto,
  plan: EmailBlastSendingDomainPlanDto | null = null,
): BlastVerdict {
  const sent = blast.Stats.Sent
  const queued = blast.Stats.Queued
  const skipped = blast.Stats.Skipped
  const failed = blast.Stats.Failed
  // Fall back to the frozen audience only before any rows exist — materialization runs a 24-hour
  // lookahead, so a scheduled blast can legitimately have none yet.
  const recipients = countRecipients(blast)
  const outstanding = queued + skipped + failed
  const notStartedYet = blast.State === 'Canary' && sent === 0

  // A finished broadcast owes its domain nothing, whatever state the domain is in now.
  const live = !isTerminalBlastState(blast.State)
  const domainState = live ? planDomainState(plan) : null
  const pacedByDomain = domainState !== null && domainState !== 'Ready'
  const days = live && plan ? buildPlanDays(plan) : []
  const heldByDomain = domainState === 'Stopped' || domainState === 'SettingUp'
  const queuedLabel =
    plan?.Domain && days.length > 0
      ? `waiting for ${plan.Domain.Domain}'s daily limit`
      : blast.State === 'Paused' || heldByDomain
      ? 'held'
      : 'queued'

  const base = {
    recipients,
    sent,
    queued,
    skipped,
    failed,
    outstanding,
    notStartedYet,
    pacedByDomain,
    days,
    queuedLabel,
    presentAs: notStartedYet ? ('Scheduled' as const) : undefined,
  }

  if (plan?.Domain && domainState === 'Stopped') {
    const trip = blast.State === 'Paused' ? latestTrip(blast) : null
    const metric = trip?.TripMetric ?? null
    const cause = domainStopCause(plan.Domain)
    const domainClause = stoppedDomainClause(plan.Domain.Domain, cause, metric, plan.RateLimits)
    // A broadcast still sending on a stopped domain is held all the same, so it reads Paused, with the
    // domain as the reason; one its own breaker paused keeps that reason first.
    return {
      ...base,
      tone: 'attention',
      presentAs: blast.State === 'Paused' ? undefined : 'Paused',
      headline:
        blast.State === 'Paused'
          ? `${pausedReason(metric, blast, trip, true)} ${asSentence(domainClause)}`
          : `Paused: ${domainClause}.`,
      guidance: stoppedDomainGuidance(cause, queued) ?? undefined,
    }
  }

  if (blast.State === 'Paused') {
    const trip = latestTrip(blast)
    const metric = trip?.TripMetric ?? null
    if (plan?.Domain && domainState === 'SettingUp') {
      return {
        ...base,
        tone: 'attention',
        headline:
          `${pausedReason(metric, blast, trip, true)} Its sending domain, ${plan.Domain.Domain}, is still being ` +
          'set up, so nothing sends until it passes setup, even after you resume.',
        guidance: pausedGuidance(metric, queued),
      }
    }
    // What resuming would actually send: the parked rows. A suppressed or guard-cancelled row is not
    // waiting on the operator and must not be counted into "held".
    const { headline, guidance } = pausedHeadline(metric, blast, queued, trip)
    return { ...base, tone: 'attention', headline, guidance }
  }

  if (blast.State === 'Complete') {
    return {
      ...base,
      tone: 'finished',
      headline:
        `Finished ${formatDateTime(blast.CompletedAtUtc)} — ${formatNumber(sent)} of ` +
        `${formatNumber(recipients)} recipients, ${formatNumber(blast.Stats.Delivered)} delivered ` +
        `(${formatPercent(blast.Stats.Delivered, sent)}).`,
    }
  }

  if (blast.State === 'Cancelled') {
    return {
      ...base,
      tone: 'stopped',
      headline:
        `Cancelled ${formatDateTime(blast.CompletedAtUtc)}. ${formatNumber(sent)} ` +
        `${sent === 1 ? 'email had' : 'emails had'} already sent; the remaining ` +
        `${formatNumber(outstanding)} were never sent and cannot be resumed.`,
    }
  }

  if (blast.State === 'Failed') {
    return {
      ...base,
      tone: 'stopped',
      headline:
        `Stopped by an error — ${formatNumber(sent)} of ${formatNumber(recipients)} sent. This send cannot ` +
        `continue and the remaining ${formatNumber(outstanding)} were not sent. Quote blast ` +
        `${blast.Id.slice(0, 8)} if you need this looked into.`,
    }
  }

  // Nothing sends from a domain in setup, so a send that has not been paused must not read as sending.
  if (plan?.Domain && domainState === 'SettingUp') {
    return {
      ...base,
      tone: 'attention',
      headline:
        `Nothing sends from ${plan.Domain.Domain} until it passes setup.` +
        (sent > 0 ? ` ${formatCount(sent)} of ${formatCount(recipients)} sent so far.` : ''),
    }
  }

  const across = days.length > 1 ? ' across several days' : ''
  const missesEvent = (plan?.Forecast?.AfterDeadline ?? 0) > 0

  // The test batch's own sentence comes first; how the warming domain spreads the list follows it.
  const withDomainPace = (headline: string): string => {
    if (!plan?.Domain || days.length === 0) return headline
    const finish = finishClause(plan)
    const waiting = plan.IsWaitingForTomorrow
      ? " Today's limit is used up, so this broadcast is waiting for tomorrow's allowance."
      : ''
    return `${headline}${waiting} It sends${across} while ${plan.Domain.Domain} warms up${finish ? `; ${finish}` : ''}.`
  }

  if (notStartedYet) {
    return {
      ...base,
      tone: missesEvent ? 'attention' : 'scheduled',
      headline: withDomainPace(
        `Nothing has sent yet. The first ${formatNumber(blast.CanarySize)} go out as a test batch, then ` +
          'the rest is released only if that batch looks healthy.',
      ),
    }
  }

  if (blast.State === 'Canary') {
    return {
      ...base,
      tone: missesEvent ? 'attention' : 'healthy',
      headline: withDomainPace(
        `Sending the test batch — ${formatNumber(sent)} of ${formatNumber(blast.CanarySize)}. The rest of ` +
          'the audience is held until this batch has been measured.',
      ),
    }
  }

  if (blast.State === 'Measuring') {
    return {
      ...base,
      tone: missesEvent ? 'attention' : 'healthy',
      headline: withDomainPace(
        'Test batch sent — measuring bounces and complaints before releasing the other ' +
          `${formatNumber(queued)}.`,
      ),
    }
  }

  // Releasing on a warming domain: where the list stands against the domain's days, and when it finishes.
  if (plan?.Domain && days.length > 0) {
    const ahead = [nextBatchClause(days), finishClause(plan)].filter(Boolean).join('; ')
    return {
      ...base,
      tone: missesEvent ? 'attention' : 'scheduled',
      headline: [
        `Sending${across} while ${plan.Domain.Domain} warms up.`,
        `${formatCount(sent)} of ${formatCount(recipients)} sent${todayClause(plan, days)}`,
        ahead ? asSentence(ahead) : '',
      ]
        .filter(Boolean)
        .join(' '),
    }
  }

  // Releasing. A resumed blast says so, because its meters judge a post-resume window while the funnel
  // below stays cumulative — without the sentence they simply look like they disagree.
  return {
    ...base,
    tone: 'healthy',
    headline: blast.ResumedAtUtc
      ? `Sending again since ${formatDateTime(blast.ResumedAtUtc)} — ${formatNumber(sent)} of ` +
        `${formatNumber(recipients)} sent. No limits breached since you resumed.`
      : `Sending normally — ${formatNumber(sent)} of ${formatNumber(recipients)} sent. No limits breached.`,
  }
}

/**
 * Where the test batch ends on the progress bar, as a percent, or null when there is no such place to
 * mark: a finished send, a batch as big as the list, or a send its domain spreads over days, whose bar
 * marks days instead.
 */
export function testBatchMarkPercent(blast: EmailBlastViewDto, verdict: BlastVerdict): number | null {
  if (verdict.pacedByDomain || isTerminalBlastState(blast.State)) return null
  if (blast.CanarySize <= 0 || blast.CanarySize >= verdict.recipients) return null
  return (blast.CanarySize / verdict.recipients) * 100
}

/** Pacing line under the spine. Null when there is nothing meaningful to say about timing. */
export function buildPacingLine(blast: EmailBlastViewDto, verdict: BlastVerdict): string | null {
  if (isTerminalBlastState(blast.State)) return null

  const perMinute = blast.EmailsPerMinute
  // Denominated on `queued`, never `outstanding`: skipped and failed rows never dispatch, so counting
  // them would overstate the ETA and keep showing one after the last sendable row had gone.
  const minutesLeft = perMinute > 0 ? Math.ceil(verdict.queued / perMinute) : null

  if (verdict.notStartedYet) {
    return (
      `Safety limits: pauses above ${blast.MaxHardBouncePercent.toFixed(2)}% hard bounces or ` +
      `${blast.MaxComplaintPercent.toFixed(2)}% complaints.`
    )
  }

  // The sending domain sets the pace, so an emails-per-minute ETA would be minutes against days. The
  // verdict and the day plan say when instead; only the measuring window keeps its own clock.
  if (verdict.pacedByDomain && blast.State !== 'Measuring') return null

  if (blast.State === 'Paused') {
    return minutesLeft === null
      ? 'Paused, so nothing is sending.'
      : `Paused, so nothing is sending. At ${formatNumber(perMinute)} emails/min it would take about ` +
        `${formatNumber(minutesLeft)} ${minutesLeft === 1 ? 'minute' : 'minutes'} to finish the remainder.`
  }

  if (blast.State === 'Measuring') {
    if (!blast.MeasurementEndsAtUtc) return 'Next check within 5 minutes.'
    const endsAt = new Date(blast.MeasurementEndsAtUtc).getTime()
    if (Number.isNaN(endsAt)) return 'Next check within 5 minutes.'
    const minutes = Math.round((endsAt - Date.now()) / 60000)
    // Past the window the engine has not run its check yet; say that rather than counting backwards.
    return minutes > 0
      ? `Releases in about ${formatNumber(minutes)} ${minutes === 1 ? 'minute' : 'minutes'}.`
      : 'Measurement window closed — the next check will release or pause this send.'
  }

  if (minutesLeft === null || verdict.queued === 0) return null
  return (
    `About ${formatNumber(perMinute)} emails/min · roughly ${formatNumber(minutesLeft)} ` +
    `${minutesLeft === 1 ? 'minute' : 'minutes'} left.`
  )
}
